import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@apollo/client/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScioMark } from "@/components/ui/scio-mark";
import { PublicHeader } from "@/components/layout/public-header";
import { LOGIN } from "@/graphql/mutations/admin";
import { RESEND_CONFIRMATION } from "@/graphql/mutations/account";

/**
 * Why a confirmation link did not work. Mirrors EmailToken::REFUSALS — the code
 * is what travels in the redirect, and the sentence lives here, so a reason can
 * never arrive in a form this page cannot render.
 */
const CONFIRM_ERRORS: Record<string, string> = {
  consumed: "Ši nuoroda jau panaudota. Jei paskyra patvirtinta, tiesiog prisijunkite.",
  expired: "Nuorodos laikas pasibaigė. Prisijunkite ir paprašysime naujos.",
  stale: "Nuoroda nebegalioja — paskyros el. paštas pasikeitė.",
  unknown: "Nuoroda neatpažinta. Patikrinkite, ar nukopijavote ją visą.",
};

const OAUTH_ERRORS: Record<string, string> = {
  account_not_found:
    "Ši Google paskyra dar neturi profilio. Sukurkite paskyrą arba paprašykite administratoriaus pridėti jūsų el. paštą.",
  missing_email: "Nepavyko gauti el. pašto iš Google paskyros.",
  authentication_failed: "Prisijungti prie Google nepavyko. Bandykite dar kartą.",
  // The server refused the intent — an unknown role, or a learner whose birth
  // date it could not use. Sending them back through the form is the fix.
  registration_invalid: "Nepavyko sukurti paskyros. Pradėkite registraciją iš naujo.",
};

/** What `login` answers. `needsConfirmation` separates „not confirmed yet" from
 *  „wrong password", which is the difference between having something to offer
 *  and having nothing. */
type LoginResult = {
  login: { success: boolean; error: string | null; needsConfirmation: boolean };
};

export default function LoginPage() {
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Only ever set by a refusal that says so — a correct password on an
  // unconfirmed address. That is what puts „atsiųsti dar kartą" on screen, so it
  // is never offered to somebody who simply mistyped their password.
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [resent, setResent] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const confirmed = searchParams.get("confirmed") === "1";
  const confirmErrorCode = searchParams.get("confirm_error");

  const oauthError = searchParams.get("error");
  const oauthMessage = oauthError
    ? OAUTH_ERRORS[oauthError] || "Prisijungti nepavyko. Bandykite dar kartą."
    : null;
  const confirmError = confirmErrorCode
    ? CONFIRM_ERRORS[confirmErrorCode] || CONFIRM_ERRORS.unknown
    : null;

  const [login, { loading }] = useMutation<LoginResult>(LOGIN, {
    onCompleted: (data) => {
      if (data.login.success) {
        navigate("/");
        window.location.reload();
      } else {
        setError(data.login.error || "Prisijungti nepavyko");
        setNeedsConfirmation(data.login.needsConfirmation);
      }
    },
    onError: () => {
      setError("Prisijungti nepavyko");
    },
  });

  // Always answers success, whatever the address — so the acknowledgement is
  // worded as „jei tokia paskyra yra", which is the only thing that is true in
  // every branch. Anything more definite would make this a way of asking which
  // addresses have accounts here.
  const [resendConfirmation, { loading: resending }] = useMutation(RESEND_CONFIRMATION, {
    onCompleted: () => setResent(true),
    onError: () => setResent(true),
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNeedsConfirmation(false);
    setResent(false);
    login({ variables: { password, email: email.trim() || null } });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* The way back to the public page — without it this card is a dead end. */}
      <PublicHeader action="login" />

      <div className="flex items-center justify-center px-4 pb-16 pt-4 sm:pt-10">
        <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          {/* The one screen where the mark stands alone, so it is labelled here
              and nowhere else — beside the word "Scio" it would be read twice. */}
          <ScioMark size={44} className="mx-auto mb-2 text-primary" title="Scio" />
          <CardTitle className="text-2xl font-bold text-center tracking-tight">
            Sveiki sugrįžę
          </CardTitle>
          <CardDescription className="text-center">
            Prisijunkite, kad pasiektumėte Scio
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Arriving from a confirmation link. First thing on the card, because it
              is the reason they are here and it is the one piece of good news this
              screen ever has. */}
          {confirmed && (
            <Alert>
              <AlertDescription>
                El. paštas patvirtintas. Dabar galite prisijungti.
              </AlertDescription>
            </Alert>
          )}

          {(oauthMessage || error || confirmError) && (
            <Alert variant="destructive">
              <AlertDescription>{oauthMessage || error || confirmError}</AlertDescription>
            </Alert>
          )}

          {/* Offered only on a refusal that said confirmation was the problem —
              which means the password was right, so this tells nobody anything
              they could not already have found out. */}
          {needsConfirmation && !resent && (
            <Button
              variant="outline"
              className="w-full"
              disabled={resending}
              onClick={() => resendConfirmation({ variables: { email: email.trim() } })}
            >
              {resending ? "Siunčiama..." : "Atsiųsti patvirtinimo nuorodą dar kartą"}
            </Button>
          )}

          {resent && (
            <Alert>
              <AlertDescription>
                Jei tokia paskyra dar nepatvirtinta, nuorodą išsiuntėme. Patikrinkite
                el. paštą, taip pat šlamšto (spam) dėžutę.
              </AlertDescription>
            </Alert>
          )}

          {/* Primary: Google sign-in (full-page redirect to OmniAuth). */}
          <Button asChild className="w-full" variant="outline">
            <a href="/users/auth/google_oauth2">
              <GoogleIcon />
              Prisijungti su Google
            </a>
          </Button>

          {/* Shown, not folded behind a link: learners and every account a
              grown-up created sign in this way, and a form they have to find
              first read as a page with only Google on it. */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            arba
            <span className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            {/* No longer „(nebūtina)". An empty address used to fall through to
                one shared instance-wide password; there is no such account any
                more, so the address is the whole of who you are. */}
            <div className="space-y-2">
              <Label htmlFor="email">El. paštas</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="vardas@pastas.lt"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
              />
              <p className="text-xs text-muted-foreground">
                Mokiniai — savo el. paštą, kurį davė tėvai ar mokytojas.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Slaptažodis</Label>
              <Input
                id="password"
                type="password"
                placeholder="Įveskite slaptažodį"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Jungiamasi..." : "Prisijungti"}
            </Button>

            {/* Inside the form rather than beside the Google button: it is only
                ever about a password, and there is no password to forget on the
                account above. */}
            <p className="text-center text-sm">
              <Link
                to="/auth/forgot"
                className="text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                Pamiršau slaptažodį
              </Link>
            </p>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            Neturite paskyros?{" "}
            <Link to="/auth/register" className="underline underline-offset-4">
              Registruotis
            </Link>
          </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.19 14.97 0 12 0 7.7 0 3.99 2.47 2.18 6.06L5.84 8.9C6.71 6.31 9.14 4.75 12 4.75z"
      />
    </svg>
  );
}
